import type { FloorEdgeOverridesTable, GeneratedFloorBoundaries } from "building-types/floors/floorAssemblyTypes";
import { mergeBayEdgeOverrides } from "building-types/floors/floorBayResolution";
import type { GeneratedBayBoundary, GeneratedBayResult } from "building-types/floors/floorBayTypes";
import { FLOOR_BAY_SPLIT_EDGE_PREFIX } from "building-types/floors/floorBayTypes";
import { matchBaysByInteriorPoint, splitLinesIntersectingPerimeter } from "building-types/floors/floorBayUtils";
import type {
  FloorBay,
  FloorSplitData,
  SplitLine,
  SplitLineEdgeOverride,
} from "building-types/floors/floorSplitDataTypes";
import {
  emptyFloorSplitData,
  DEFAULT_SPLIT_LINE_THICKNESS,
  DEFAULT_SPLIT_LINE_SPLICE_LENGTH,
  SplitLineJoistAlignment,
  SplitLineSupportFramingCondition,
} from "building-types/floors/floorSplitDataTypes";
import type { ConstraintLine } from "building-types/framing/floorFramingConstraintTypes";
import { ConstraintLineType } from "building-types/framing/floorFramingConstraintTypes";
import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import type { LumberProfileType } from "building-types/framing/lumberTypes";
import type { Table } from "common/data/table";
import { splitPerimeterBySplitLines } from "doc/elements/floor/floorPerimeterGeometry";
import { distPointToSegment } from "geom/closestPoint";
import { classifyContainmentVec2PolyLine2Fast } from "geom/containment";
import { polyLine2EdgeCount, polyline2PointCloudCentroid } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";

const SPLIT_LINE_ADJACENCY_OFFSET = 1.75;

function canonicalizeConstraintSegmentPolyline(polyline: PolyLine2): PolyLine2 {
  const x0 = polyline[0];
  const y0 = polyline[1];
  const x1 = polyline[2];
  const y1 = polyline[3];
  if (x1 < x0 || (x1 === x0 && y1 < y0)) {
    return polyLine2New([x1, y1, x0, y0]);
  }
  return polyline;
}

function buildBayEdgeIds(
  bayPerimeter: PolyLine2,
  originalPerimeter: PolyLine2,
  originalEdgeIds: string[],
  splitLines: SplitLine[],
): string[] {
  const bayEdgeCount = bayPerimeter.length / 2 - 1;
  const edgeIds: string[] = [];
  const splitCounters: { [originalEdgeId: string]: number } = {};

  for (let i = 0; i < bayEdgeCount; i++) {
    const mx = (bayPerimeter[i * 2] + bayPerimeter[(i + 1) * 2]) / 2;
    const my = (bayPerimeter[i * 2 + 1] + bayPerimeter[(i + 1) * 2 + 1]) / 2;

    let bestEdgeId: string | undefined;
    let bestDist = Infinity;

    const origEdgeCount = originalPerimeter.length / 2 - 1;
    for (let j = 0; j < origEdgeCount; j++) {
      const ox0 = originalPerimeter[j * 2];
      const oy0 = originalPerimeter[j * 2 + 1];
      const ox1 = originalPerimeter[(j + 1) * 2];
      const oy1 = originalPerimeter[(j + 1) * 2 + 1];
      const dist = distPointToSegment(mx, my, ox0, oy0, ox1, oy1);
      if (dist < bestDist) {
        bestDist = dist;
        bestEdgeId = originalEdgeIds[j] ?? String(j);
      }
    }

    for (const sl of splitLines) {
      const slPl = sl.polyline;
      const slEdgeCount = slPl.length / 2 - 1;
      for (let k = 0; k < slEdgeCount; k++) {
        const sx0 = slPl[k * 2];
        const sy0 = slPl[k * 2 + 1];
        const sx1 = slPl[(k + 1) * 2];
        const sy1 = slPl[(k + 1) * 2 + 1];
        const dist = distPointToSegment(mx, my, sx0, sy0, sx1, sy1);
        if (dist < bestDist) {
          bestDist = dist;
          bestEdgeId = `splitline-${sl.id}-edge-${k}`;
        }
      }
    }

    if (bestEdgeId !== undefined && bestDist < 1e-3) {
      if (bestEdgeId.startsWith("splitline-")) {
        edgeIds.push(bestEdgeId);
      } else {
        const counter = (splitCounters[bestEdgeId] ?? 0) + 1;
        splitCounters[bestEdgeId] = counter;
        if (counter > 1) {
          edgeIds.push(`${FLOOR_BAY_SPLIT_EDGE_PREFIX}${bestEdgeId}-${counter}`);
        } else {
          edgeIds.push(bestEdgeId);
        }
      }
    } else {
      edgeIds.push(`bay-edge-${i}`);
    }
  }

  return edgeIds;
}

export function generateFloorBays(
  perimeter: PolyLine2,
  openings: PolyLine2[],
  perimeterEdgeIds: string[],
  openingEdgeIds: string[],
  splitData: FloorSplitData,
  parentFloorId: string,
  parentFloorEdgeOverrides?: FloorEdgeOverridesTable,
  parentFloorOpeningEdgeOverrides?: FloorEdgeOverridesTable,
  mirror: boolean = false,
  mirrorTranslationX: number = 0,
): GeneratedBayResult {
  const splitLines = Object.values(splitData.splitLines ?? {});
  const baySplittingSplitLines = splitLines.filter(splitLineSplitsBays);

  if (splitLines.length === 0) {
    return { bayBoundaries: [], constraintLines: [] };
  }

  if (baySplittingSplitLines.length === 0) {
    const constraintLines = buildConstraintLinesFromSplitLines(splitLines);
    return { bayBoundaries: [], constraintLines };
  }

  const normalizedBayPolylines = splitPerimeterBySplitLines(perimeter, baySplittingSplitLines);
  if (normalizedBayPolylines.length <= 1) {
    const constraintLines = buildConstraintLinesFromSplitLines(splitLines);
    return { bayBoundaries: [], constraintLines };
  }

  const existingBays = splitData.bays ?? {};
  const { bayIds, updatedBays } = matchBaysByInteriorPoint(
    normalizedBayPolylines,
    existingBays,
    mirror,
    mirrorTranslationX,
  );
  const constraintLines = buildConstraintLinesFromSplitLines(splitLines, normalizedBayPolylines, bayIds);

  const bayBoundaries: GeneratedBayBoundary[] = [];

  for (let i = 0; i < normalizedBayPolylines.length; i++) {
    const bayPl = normalizedBayPolylines[i];
    const bayId = bayIds[i];
    const bayDef = updatedBays[bayId] ?? existingBays[bayId];

    const bayOpenings = classifyOpeningsIntoBay(bayPl, openings);
    const bayEdgeIds = buildBayEdgeIds(bayPl, perimeter, perimeterEdgeIds, baySplittingSplitLines);
    const bayOpeningEdgeIds = buildBayOpeningEdgeIds(bayOpenings, openings, openingEdgeIds);

    const mergedEdgeOverrides = mergeBayEdgeOverrides(bayEdgeIds, bayDef?.bayEdgeOverrides, parentFloorEdgeOverrides);

    const perimeterEdgeCount = polyLine2EdgeCount(bayPl);
    const openingEdgeCounts = bayOpenings.map(polyLine2EdgeCount);

    const boundary: GeneratedFloorBoundaries = {
      perimeters: [bayPl],
      openings: bayOpenings,
      edgeIds: bayEdgeIds,
      perimeterEdgeCounts: [perimeterEdgeCount],
      openingEdgeIds: bayOpeningEdgeIds,
      openingEdgeCounts: openingEdgeCounts,
      floorEdgeOverrides: mergedEdgeOverrides,
      floorOpeningEdgeOverrides: parentFloorOpeningEdgeOverrides ?? {},
      splitData: splitData,
      parentFloorId,
      bayId,
      splitLineConstraints: constraintLines.length > 0 ? constraintLines : undefined,
    };

    bayBoundaries.push({ bayId, boundary });
  }

  return { bayBoundaries, constraintLines };
}

function classifyOpeningsIntoBay(bayPerimeter: PolyLine2, openings: PolyLine2[]): PolyLine2[] {
  const result: PolyLine2[] = [];
  for (const opening of openings) {
    const centroid = polyline2PointCloudCentroid(opening);
    if (centroid && classifyContainmentVec2PolyLine2Fast(centroid[0], centroid[1], bayPerimeter)) {
      result.push(opening);
    }
  }
  return result;
}

function buildBayOpeningEdgeIds(
  bayOpenings: PolyLine2[],
  allOpenings: PolyLine2[],
  allOpeningEdgeIds: string[],
): string[] {
  const result: string[] = [];
  let openingEdgeOffset = 0;
  for (let oi = 0; oi < allOpenings.length; oi++) {
    const opening = allOpenings[oi];
    const edgeCount = opening.length / 2 - 1;
    if (bayOpenings.includes(opening)) {
      for (let ei = 0; ei < edgeCount; ei++) {
        result.push(allOpeningEdgeIds[openingEdgeOffset + ei] ?? `opening-${oi}-edge-${ei}`);
      }
    }
    openingEdgeOffset += edgeCount;
  }
  return result;
}

export function buildConstraintLinesFromSplitLines(
  splitLines: readonly SplitLine[],
  bayPolygons?: readonly PolyLine2[],
  bayIds?: readonly FloorBay["id"][],
): ConstraintLine[] {
  const constraints: ConstraintLine[] = [];
  for (const sl of splitLines) {
    const edgeOverrides = sl.edgeOverrides ?? {};
    const edgeCount = sl.polyline.length / 2 - 1;
    for (let i = 0; i < edgeCount; i++) {
      const edgeId = sl.edgeIds[i] ?? String(i);
      const override = edgeOverrides[edgeId];
      const framingComponentId = override?.framingComponentId;
      const framingProfile = override?.framingProfile;
      const thickness = resolveSplitLineThickness(framingProfile);
      const spliceLength = resolveSplitLineSpliceLength(override);

      const segPl = canonicalizeConstraintSegmentPolyline(
        polyLine2New([
          sl.polyline[i * 2],
          sl.polyline[i * 2 + 1],
          sl.polyline[(i + 1) * 2],
          sl.polyline[(i + 1) * 2 + 1],
        ]),
      );
      const constraintType = isFramingLineOverride(override)
        ? ConstraintLineType.FRAMING_LINE
        : ConstraintLineType.SUPPORT_LINE;
      const adjacency = resolveConstraintLineBayAdjacency(segPl, bayPolygons, bayIds, constraintType);

      constraints.push({
        type: constraintType,
        polyline: segPl,
        width: thickness,
        joistAlignment: override?.joistAlignment ?? SplitLineJoistAlignment.FLUSH_TOP,
        supportFramingCondition: override?.supportFramingCondition ?? SplitLineSupportFramingCondition.RUN_OVER,
        framingComponentId,
        ...(spliceLength !== undefined ? { spliceLength } : {}),
        ...adjacency,
      });
    }
  }
  return constraints;
}

function resolveConstraintLineBayAdjacency(
  polyline: PolyLine2,
  bayPolygons: readonly PolyLine2[] | undefined,
  bayIds: readonly FloorBay["id"][] | undefined,
  constraintType: ConstraintLineType,
): Pick<ConstraintLine, "bayId" | "bayLeftId" | "bayRightId"> {
  if (!bayPolygons || !bayIds || bayPolygons.length === 0 || bayIds.length === 0) {
    return {};
  }

  const x0 = polyline[0];
  const y0 = polyline[1];
  const x1 = polyline[2];
  const y1 = polyline[3];
  const dx = x1 - x0;
  const dy = y1 - y0;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return {};
  }

  const midX = (x0 + x1) * 0.5;
  const midY = (y0 + y1) * 0.5;
  const normalX = -dy / length;
  const normalY = dx / length;
  const bayId =
    constraintType === ConstraintLineType.SUPPORT_LINE
      ? findBayContainingPoint(midX, midY, bayPolygons, bayIds)
      : undefined;

  const bayLeftId = findBayContainingPoint(
    midX + normalX * SPLIT_LINE_ADJACENCY_OFFSET,
    midY + normalY * SPLIT_LINE_ADJACENCY_OFFSET,
    bayPolygons,
    bayIds,
  );
  const bayRightId = findBayContainingPoint(
    midX - normalX * SPLIT_LINE_ADJACENCY_OFFSET,
    midY - normalY * SPLIT_LINE_ADJACENCY_OFFSET,
    bayPolygons,
    bayIds,
  );

  return {
    ...(bayId !== undefined ? { bayId } : {}),
    ...(bayLeftId !== undefined ? { bayLeftId } : {}),
    ...(bayRightId !== undefined ? { bayRightId } : {}),
  };
}

function assignConstraintLinesToBay(
  constraintLines: readonly ConstraintLine[] | undefined,
  bayPolygon: PolyLine2,
  bayId: FloorBay["id"],
): ConstraintLine[] | undefined {
  if (!constraintLines || constraintLines.length === 0) {
    return undefined;
  }

  const result: ConstraintLine[] = [];
  for (const constraint of constraintLines) {
    if (constraint.bayId !== undefined) {
      if (constraint.bayId === bayId) {
        result.push(constraint);
      }
      continue;
    }

    const assignedConstraint: ConstraintLine = {
      ...constraint,
      ...resolveConstraintLineBayAdjacency(constraint.polyline, [bayPolygon], [bayId], constraint.type),
    };
    if (assignedConstraint.bayId === bayId) {
      result.push(assignedConstraint);
    }
  }

  return result.length > 0 ? result : undefined;
}

function findBayContainingPoint(
  x: number,
  y: number,
  bayPolygons: readonly PolyLine2[],
  bayIds: readonly FloorBay["id"][],
): FloorBay["id"] | undefined {
  for (let i = 0; i < bayPolygons.length; i++) {
    if (classifyContainmentVec2PolyLine2Fast(x, y, bayPolygons[i])) {
      return bayIds[i];
    }
  }
  return undefined;
}

function resolveSplitLineThickness(framingProfile: LumberProfileType | undefined): number {
  if (!framingProfile) {
    return DEFAULT_SPLIT_LINE_THICKNESS;
  }
  return LumberProfileCatalog[framingProfile]?.height ?? DEFAULT_SPLIT_LINE_THICKNESS;
}

function resolveSplitLineSpliceLength(override: SplitLineEdgeOverride | undefined): number | undefined {
  if (
    override?.joistAlignment === SplitLineJoistAlignment.DROPPED &&
    override.supportFramingCondition === SplitLineSupportFramingCondition.LAP_JOINT
  ) {
    return override.spliceLength ?? DEFAULT_SPLIT_LINE_SPLICE_LENGTH;
  }
  return undefined;
}

function isFramingLineOverride(override: SplitLineEdgeOverride | undefined): boolean {
  const joistAlignment = override?.joistAlignment ?? SplitLineJoistAlignment.FLUSH_TOP;
  if (joistAlignment === SplitLineJoistAlignment.FLUSH_TOP || joistAlignment === SplitLineJoistAlignment.FLUSH_BOTTOM) {
    return true;
  }
  return (
    joistAlignment === SplitLineJoistAlignment.DROPPED &&
    override?.supportFramingCondition === SplitLineSupportFramingCondition.LAP_JOINT
  );
}

function splitLineSplitsBays(splitLine: SplitLine): boolean {
  const edgeOverrides = splitLine.edgeOverrides ?? {};
  const edgeCount = splitLine.polyline.length / 2 - 1;
  for (let i = 0; i < edgeCount; i++) {
    const edgeId = splitLine.edgeIds[i] ?? String(i);
    if (isFramingLineOverride(edgeOverrides[edgeId])) {
      return true;
    }
  }
  return false;
}

export function createBaysFromPerimeters(
  parentBoundary: GeneratedFloorBoundaries,
  splitData: FloorSplitData | undefined,
  floorId: string,
  mirror: boolean = false,
  mirrorTranslationX: number = 0,
): GeneratedBayBoundary[] {
  if (parentBoundary.perimeters.length === 0) {
    return [];
  }

  const effectiveSplitData = splitData ?? emptyFloorSplitData;
  const allSplitLines = Object.values(effectiveSplitData.splitLines ?? {});

  if (allSplitLines.length === 0) {
    return createBaysFromPerimetersNoSplit(
      parentBoundary,
      effectiveSplitData.bays ?? {},
      floorId,
      mirror,
      mirrorTranslationX,
    );
  }

  const allBayBoundaries: GeneratedBayBoundary[] = [];
  const claimedBayIds = new Set<string>();
  let edgeIdOffset = 0;

  for (let pi = 0; pi < parentBoundary.perimeters.length; pi++) {
    const perimeter = parentBoundary.perimeters[pi];
    const edgeCount = parentBoundary.perimeterEdgeCounts[pi] ?? 0;
    const perimeterEdgeIds = parentBoundary.edgeIds.slice(edgeIdOffset, edgeIdOffset + edgeCount);
    edgeIdOffset += edgeCount;

    const relevantSplitLines = splitLinesIntersectingPerimeter(perimeter, allSplitLines);

    if (relevantSplitLines.length > 0) {
      // Build a per-perimeter splitData containing only the relevant split lines
      // and only the bay definitions not already claimed by a previous perimeter.
      const relevantSplitLineMap: Record<string, SplitLine> = {};
      for (const sl of relevantSplitLines) {
        relevantSplitLineMap[sl.id] = sl;
      }
      const unclaimedBays: Record<string, FloorBay> = {};
      for (const [id, bay] of Object.entries(effectiveSplitData.bays ?? {})) {
        if (!claimedBayIds.has(id)) {
          unclaimedBays[id] = bay;
        }
      }
      const perSplitData: FloorSplitData = {
        splitLines: relevantSplitLineMap,
        bays: unclaimedBays,
      };

      const result = generateFloorBays(
        perimeter,
        parentBoundary.openings,
        perimeterEdgeIds,
        parentBoundary.openingEdgeIds,
        perSplitData,
        floorId,
        parentBoundary.floorEdgeOverrides,
        parentBoundary.floorOpeningEdgeOverrides,
        mirror,
        mirrorTranslationX,
      );

      if (result.bayBoundaries.length > 0) {
        for (const bay of result.bayBoundaries) {
          claimedBayIds.add(bay.bayId);
          allBayBoundaries.push(bay);
        }
      } else {
        const singlePerimBoundary: GeneratedFloorBoundaries = {
          ...parentBoundary,
          perimeters: [perimeter],
          edgeIds: perimeterEdgeIds,
          perimeterEdgeCounts: [edgeCount],
          splitLineConstraints: result.constraintLines.length > 0 ? result.constraintLines : undefined,
        };
        const unsplitBays = createBaysFromPerimetersNoSplit(
          singlePerimBoundary,
          unclaimedBays,
          floorId,
          mirror,
          mirrorTranslationX,
        );
        for (const bay of unsplitBays) {
          claimedBayIds.add(bay.bayId);
          allBayBoundaries.push(bay);
        }
      }
    } else {
      // No split lines intersect this perimeter — treat it as a single unsplit bay.
      const unclaimedBays: Record<string, FloorBay> = {};
      for (const [id, bay] of Object.entries(effectiveSplitData.bays ?? {})) {
        if (!claimedBayIds.has(id)) {
          unclaimedBays[id] = bay;
        }
      }
      const singlePerimBoundary: GeneratedFloorBoundaries = {
        ...parentBoundary,
        perimeters: [perimeter],
        edgeIds: perimeterEdgeIds,
        perimeterEdgeCounts: [edgeCount],
      };
      const unsplitBays = createBaysFromPerimetersNoSplit(
        singlePerimBoundary,
        unclaimedBays,
        floorId,
        mirror,
        mirrorTranslationX,
      );
      for (const bay of unsplitBays) {
        claimedBayIds.add(bay.bayId);
        allBayBoundaries.push(bay);
      }
    }
  }

  return allBayBoundaries;
}

export function createBaysFromPerimetersNoSplit(
  parentBoundary: GeneratedFloorBoundaries,
  existingBays: Table<FloorBay>,
  floorId: string,
  mirror: boolean = false,
  mirrorTranslationX: number = 0,
): GeneratedBayBoundary[] {
  const { perimeters, edgeIds, perimeterEdgeCounts, floorEdgeOverrides, floorOpeningEdgeOverrides } = parentBoundary;

  const { bayIds, updatedBays } = matchBaysByInteriorPoint(perimeters, existingBays, mirror, mirrorTranslationX);
  const result: GeneratedBayBoundary[] = [];

  let edgeIdOffset = 0;
  for (let pi = 0; pi < perimeters.length; pi++) {
    const perimeter = perimeters[pi];
    const edgeCount = perimeterEdgeCounts[pi];
    const bayEdgeIds = edgeIds.slice(edgeIdOffset, edgeIdOffset + edgeCount);
    edgeIdOffset += edgeCount;

    const bayOpenings = classifyOpeningsIntoBay(perimeter, parentBoundary.openings);
    const bayOpeningEdgeIds = buildBayOpeningEdgeIds(
      bayOpenings,
      parentBoundary.openings,
      parentBoundary.openingEdgeIds,
    );
    const bayOpeningEdgeCounts = bayOpenings.map(polyLine2EdgeCount);

    const bayDef = updatedBays[bayIds[pi]] ?? existingBays[bayIds[pi]];
    const bayEdgeOverrides = mergeBayEdgeOverrides(bayEdgeIds, bayDef?.bayEdgeOverrides, floorEdgeOverrides);

    const boundary: GeneratedFloorBoundaries = {
      perimeters: [perimeter],
      openings: bayOpenings,
      edgeIds: bayEdgeIds,
      perimeterEdgeCounts: [edgeCount],
      openingEdgeIds: bayOpeningEdgeIds,
      openingEdgeCounts: bayOpeningEdgeCounts,
      floorEdgeOverrides: bayEdgeOverrides,
      floorOpeningEdgeOverrides: floorOpeningEdgeOverrides ?? {},
      splitData: { splitLines: {}, bays: existingBays },
      parentFloorId: floorId,
      bayId: bayIds[pi],
      splitLineConstraints: assignConstraintLinesToBay(parentBoundary.splitLineConstraints, perimeter, bayIds[pi]),
    };

    result.push({ bayId: bayIds[pi], boundary });
  }

  return result;
}
